import { z } from 'zod';

import { ReferenceArea, ReferenceLine } from '#core/components/dashboard/schema.annotations.ts';
import { columnRef, inputDataset } from '#core/components/dashboard/schema.dataset.ts';
import {
  FillOpacity,
  GridMode,
  ItemBase,
  NumericAxis,
  SeriesColor,
  StrokeStyle,
} from '#core/components/dashboard/schema.primitives.ts';

const CurveType = z.enum(['linear', 'smooth', 'step']);

const CartesianAxisX = z.object({
  kind: z
    .enum(['time', 'category', 'numeric'])
    .optional()
    .describe(
      'Determines x value semantics. Omitted by default — derived from the type of the column(s) ' +
        'referenced by `series[*].data.x` (datetime → time, string → category, number → numeric). ' +
        'Set explicitly only to override the inferred kind.',
    ),
  label: z.string().optional(),
  scale: z.enum(['linear', 'log']).optional().describe('Only applies when resolved kind is numeric.'),
  unit: z.string().optional().describe('Only applies when resolved kind is numeric.'),
  zeroReference: z.boolean().optional().describe('Only applies when resolved kind is numeric.'),
});

const SeriesData = z
  .object({
    x: columnRef.describe('Column carrying x-axis values. Column type determines xAxis kind when not set explicitly.'),
    y: columnRef.describe('Column carrying numeric y-axis values.'),
    min: columnRef
      .optional()
      .describe(
        'Lower band edge. Presence auto-renders a band — no separate flag is needed. ' +
          'If only `min` is set (no `max`), the band spans from y to min on each point.',
      ),
    max: columnRef
      .optional()
      .describe(
        'Upper band edge. Presence auto-renders a band. ' +
          'If only `max` is set (no `min`), the band spans from y to max on each point.',
      ),
  })
  .describe('Column-role mapping for a cartesian series.');

const Series = z.object({
  id: z.string(),
  label: z.string().optional().describe('Display label; defaults to the y column name when omitted.'),
  color: SeriesColor.optional().describe('Auto-assigned from palette when omitted.'),
  type: z.enum(['line', 'bar', 'area']).default('line'),
  axis: z.enum(['left', 'right']).default('left'),
  inputDataset,
  data: SeriesData,
  strokeStyle: StrokeStyle.default('solid'),
  curve: CurveType.default('smooth').describe('line/area only; ignored otherwise.'),
  // area fill opacity or band opacity (when min/max present)
  fillOpacity: FillOpacity.default('medium').describe('area and band only; ignored otherwise.'),
});

export const CartesianSchema = ItemBase.extend({
  type: z.literal('cartesian'),
  xAxis: CartesianAxisX.prefault({}),
  yAxisLeft: NumericAxis.prefault({}),
  yAxisRight: NumericAxis.optional().describe('Auto-created when any series uses axis="right".'),
  series: Series.array().min(1).max(8),
  orientation: z
    .enum(['vertical', 'horizontal'])
    .default('vertical')
    .describe(
      'Horizontal swaps the role of x and y axes — useful for ranked bar comparisons with long category labels.',
    ),
  stackMode: z
    .enum(['none', 'absolute', 'percent'])
    .default('none')
    .describe('Applies uniformly to bar and area series in this chart.'),
  referenceLines: ReferenceLine.array().prefault([]),
  referenceAreas: ReferenceArea.array().prefault([]),
  grid: GridMode.default('horizontal'),
});

/** @public Input type for CartesianSchema - use for component props */
export type CartesianSchemaInput = z.input<typeof CartesianSchema>;

/** @public Output type for CartesianSchema - use after Zod parsing */
export type CartesianSchemaOutput = z.output<typeof CartesianSchema>;
